import { useEffect, useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  Col,
  Collapse,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
} from 'reactstrap';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BreadCrumb from 'Components/Common/BreadCrumb';
import {
  BarcodeOptions,
  SettingsAccessControl,
  StoreConfigFlags,
} from 'types/settings/settingsApiTypes';
import { getSettings, getSettingsAccess } from 'services/settingsService';
import { apiToForm } from 'utils/mappers/settingsMapper';
import { SettingsProvider } from 'pages/SettingsV2/SettingsContext';
import { INITIAL_VALUE } from 'pages/SettingsV2/InitialValue';
import {
  BARCODE_BRAND_OPTIONS,
  BARCODE_SIZE_OPTIONS,
} from 'pages/SettingsV2/SettingsConstants';
import { SettingsFormValues } from 'pages/SettingsV2/settingsTypes';
import {
  SettingsV2SectionKey,
  SETTINGS_SECTION_GROUPS,
  SETTINGS_SECTIONS,
} from 'pages/SettingsV2/settingsSections';

interface LoadedSettings {
  values: SettingsFormValues;
  flags: StoreConfigFlags;
  access: SettingsAccessControl;
  barcodeOptions: BarcodeOptions | null;
}

const DEFAULT_FLAGS: StoreConfigFlags = {
  show_quotation: true,
  show_rounding: true,
  show_points: true,
  show_service_charge: true,
  show_doc_template: true,
};

const DEFAULT_BARCODE_OPTIONS: BarcodeOptions = {
  brands: BARCODE_BRAND_OPTIONS,
  sizes_by_type: {
    '1': BARCODE_SIZE_OPTIONS.map((option) => option.value),
    '2': BARCODE_SIZE_OPTIONS.map((option) => option.value),
    '3': BARCODE_SIZE_OPTIONS.map((option) => option.value),
    '4': BARCODE_SIZE_OPTIONS.map((option) => option.value),
    '5': BARCODE_SIZE_OPTIONS.map((option) => option.value),
  },
  size_labels: {
    see_dlabel: Object.fromEntries(
      BARCODE_SIZE_OPTIONS.map((option) => [option.value, option.label])
    ),
    click_label: Object.fromEntries(
      BARCODE_SIZE_OPTIONS.map((option) => [option.value, option.label])
    ),
  },
};

const DEFAULT_ACCESS: SettingsAccessControl = {
  beam_qr_api_payments: false,
  kitchen_ticket_printing: false,
  points: false,
  rounding_bill: false,
  service_charge: false,
};

// Mock access for local UI testing.
const USE_MOCK_SETTINGS_ACCESS = false;

const MOCK_SETTINGS_ACCESS: SettingsAccessControl = {
  beam_qr_api_payments: false,
  kitchen_ticket_printing: false,
  points: false,
  rounding_bill: false,
  service_charge: false,
};

const FALLBACK_SETTINGS: LoadedSettings = {
  values: INITIAL_VALUE,
  flags: DEFAULT_FLAGS,
  access: DEFAULT_ACCESS,
  barcodeOptions: DEFAULT_BARCODE_OPTIONS,
};

const SettingsPageV2 = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const invEnabled = searchParams.get('inv') === '1';

  const [loaded, setLoaded] = useState<LoadedSettings>(FALLBACK_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeSection, setActiveSection] =
    useState<SettingsV2SectionKey>('productPrice');
  const [serviceConnectionsViewVersion, setServiceConnectionsViewVersion] =
    useState(0);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {
      programSettings: true,
    }
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(null);

      try {
        const [settingsRes, accessRes] = await Promise.allSettled([
          getSettings(),
          getSettingsAccess(),
        ]);
        if (cancelled) return;

        const res: any =
          settingsRes.status === 'fulfilled' ? settingsRes.value : null;
        const accessFromApi =
          accessRes.status === 'fulfilled' &&
          accessRes.value?.error_code === 0 &&
          accessRes.value.data
            ? accessRes.value.data
            : DEFAULT_ACCESS;
        const access = USE_MOCK_SETTINGS_ACCESS
          ? MOCK_SETTINGS_ACCESS
          : accessFromApi;

        if (res?.error_code === 0 && res.data) {
          setLoaded({
            values: apiToForm(res.data),
            flags: res.data.flags,
            access,
            barcodeOptions: res.data.barcode_options,
          });
        } else if (res?.error_code === 0) {
          setLoaded({ ...FALLBACK_SETTINGS, access });
        } else {
          setLoadError(res?.message || 'Failed to load settings');
        }
      } catch (err: any) {
        if (!cancelled) {
          setLoadError(err?.message || 'Failed to load settings');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleSections = useMemo(
    () =>
      SETTINGS_SECTIONS.filter((section) =>
        section.visible(loaded.flags, invEnabled)
      ),
    [loaded.flags, invEnabled]
  );

  const visibleSectionGroups = useMemo(
    () =>
      SETTINGS_SECTION_GROUPS.map((group) => ({
        ...group,
        sections: visibleSections.filter(
          (section) => section.groupKey === group.key
        ),
      })).filter((group) => group.sections.length > 0),
    [visibleSections]
  );

  const standaloneSections = useMemo(
    () => visibleSections.filter((section) => section.standalone),
    [visibleSections]
  );

  useEffect(() => {
    if (
      visibleSections.length > 0 &&
      !visibleSections.some((section) => section.key === activeSection)
    ) {
      setActiveSection(visibleSections[0].key);
    }
  }, [visibleSections, activeSection]);

  useEffect(() => {
    const activeGroupKey = visibleSections.find(
      (section) => section.key === activeSection
    )?.groupKey;

    if (!activeGroupKey) {
      return;
    }

    setExpandedGroups((prev) =>
      prev[activeGroupKey] ? prev : { ...prev, [activeGroupKey]: true }
    );
  }, [visibleSections, activeSection]);

  const ActiveSectionComponent = (
    visibleSections.find((section) => section.key === activeSection) ??
    visibleSections[0]
  )?.component;

  const handleSectionSelect = (sectionKey: SettingsV2SectionKey) => {
    if (sectionKey === 'serviceConnections' && activeSection === sectionKey) {
      setServiceConnectionsViewVersion((prev) => prev + 1);
    }
    setActiveSection(sectionKey);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t('General Settings')} />

        {loading ? (
          <div className="d-flex justify-content-center py-3">
            <Spinner color="primary" size="sm" />
          </div>
        ) : null}

        {loadError ? (
          <div className="alert alert-danger" role="alert">
            {t(loadError)}
          </div>
        ) : null}

        <Formik
          initialValues={loaded.values}
          enableReinitialize
          onSubmit={() => undefined}
        >
          <Form>
            <SettingsProvider
              flags={loaded.flags}
              access={loaded.access}
              barcodeOptions={loaded.barcodeOptions}
              initialData={loaded.values}
            >
              <Row className={`g-3 ${loading ? 'opacity-50 pe-none' : ''}`}>
                <Col xs={12} md={3}>
                  <Card className="shadow-none border mb-0">
                    <CardBody className="p-2">
                      <div className="d-flex flex-column gap-2">
                        {visibleSectionGroups.map((group) => {
                          const isExpanded = expandedGroups[group.key] ?? true;
                          const hasActiveSection = group.sections.some(
                            (section) => section.key === activeSection
                          );

                          return (
                            <div
                              key={group.key}
                              className="border rounded overflow-hidden"
                            >
                              <button
                                type="button"
                                className={`btn w-100 text-start border-0 rounded-0 d-flex align-items-center justify-content-between px-3 py-2 ${
                                  hasActiveSection
                                    ? 'btn-soft-primary'
                                    : 'btn-light'
                                }`}
                                onClick={() =>
                                  setExpandedGroups((prev) => ({
                                    ...prev,
                                    [group.key]: !isExpanded,
                                  }))
                                }
                              >
                                <span className="d-inline-flex align-items-center">
                                  <i
                                    className={`${group.icon} align-middle me-2`}
                                  />
                                  {t(group.labelKey)}
                                </span>
                                <i
                                  className={`ri-arrow-${
                                    isExpanded ? 'up' : 'down'
                                  }-s-line fs-5`}
                                />
                              </button>

                              <Collapse isOpen={isExpanded}>
                                <Nav pills className="flex-column gap-1 p-2">
                                  {group.sections.map((section) => (
                                    <NavItem
                                      key={section.key}
                                      className="flex-shrink-0"
                                    >
                                      <NavLink
                                        href="#"
                                        active={activeSection === section.key}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleSectionSelect(section.key);
                                        }}
                                      >
                                        <i
                                          className={`${section.icon} align-middle me-2`}
                                        />
                                        {t(section.labelKey)}
                                      </NavLink>
                                    </NavItem>
                                  ))}
                                </Nav>
                              </Collapse>
                            </div>
                          );
                        })}

                        {standaloneSections.map((section) => (
                          <button
                            key={section.key}
                            type="button"
                            className={`btn w-100 text-start border rounded d-flex align-items-center px-3 py-2 ${
                              activeSection === section.key
                                ? 'btn-soft-primary'
                                : 'btn-light'
                            }`}
                            onClick={() => handleSectionSelect(section.key)}
                          >
                            <i
                              className={`${section.icon} align-middle me-2`}
                            />
                            {t(section.labelKey)}
                          </button>
                        ))}
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col xs={12} md={9}>
                  {ActiveSectionComponent ? (
                    activeSection === 'serviceConnections' ? (
                      <ActiveSectionComponent
                        key={`serviceConnections-${serviceConnectionsViewVersion}`}
                        providerAccess={{
                          beam: loaded.access.beam_qr_api_payments,
                        }}
                      />
                    ) : (
                      <ActiveSectionComponent key={activeSection} />
                    )
                  ) : null}
                </Col>
              </Row>
            </SettingsProvider>
          </Form>
        </Formik>
      </Container>

      <ToastContainer closeButton={false} limit={1} />
    </div>
  );
};

export default SettingsPageV2;
